import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useScreenQuery } from 'playup-web-core';
import TagManager from 'react-gtm-module';

import Footer from './components/Footer/Footer';
import Notifications from './components/Notifications';
import RouteModal from './components/RouteModal';
import { SocketContext } from './contexts/SocketContext';
import usePortalGlobal from './hooks/usePortalGlobal';
import AppRouter from './AppRouter';
import appConfig from './appConfig';
import PortalSessionContext from './contexts/PortalSessionContext/PortalSessionContext';
import useNotify from './hooks/useNotify';
import Navbar from './components/Navbar/Navbar';

const App = () => {
	const { refreshPortalUser, user } = React.useContext(PortalSessionContext);
	const { bindAuthChannelEvent } = React.useContext(SocketContext);
	const { isSmall } = useScreenQuery();
	const notify = useNotify();
	const [currentPathname, setCurrentPathname] = React.useState(location.pathname);
	usePortalGlobal(user);

	// listening for pathname changes
	React.useEffect(() => {
		const interval = setInterval(() => {
			setCurrentPathname(location.pathname);
		}, 500);
		return () => clearInterval(interval);
	}, []);

	// Setting socket listener for account balance updates, when an update is received by the socket
	// refresh the user in the context
	React.useEffect(() => {
		if (!user?.id) return;

		const unbindUpdates = bindAuthChannelEvent(`private-user.${user.id}`, 'wallet.balance.updated', refreshPortalUser);

		return unbindUpdates;
	}, [bindAuthChannelEvent, user?.id, refreshPortalUser]);

	React.useEffect(() => {
		if (!user?.id) return;
		const unbindDepositUpdates = bindAuthChannelEvent(`private-user.${user.id}`, 'notification.deposited', (event) => {
			const message = event.message;
			notify({
				type: 'success',
				title: 'Deposit Successful',
				message: message,
				timeout: 5000,
			});
		});
		return unbindDepositUpdates;
	}, [bindAuthChannelEvent, user?.id, notify]);

	React.useEffect(() => {
		if (appConfig.googleTagManagerID)
			TagManager.dataLayer({
				dataLayer: {
					event: 'user_details',
					user_id: user?.pin,
					first_name: user?.first_name,
					last_name: user?.last_name,
					user_name: user?.name,
					email: user?.email,
					loggedIn: !!user?.id,
				},
			});
	}, [user?.email, user?.first_name, user?.id, user?.last_name, user?.name, user?.pin]);

	return (
		<>
			<Helmet titleTemplate={`${appConfig.brand.name} | %s`} />
			<Navbar />
			<Notifications />
			<main id="app">
				<AppRouter />
			</main>
			<RouteModal isSignedIn={false} smallScreen={isSmall} />
			{(!isSmall || currentPathname.match(/\/betting\/?$/)) && <Footer />}
		</>
	);
};

export default App;
